'use client';

import {
  DependencyList,
  EffectCallback,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  createScript,
  deleteScript,
  factoryCookie,
  FactoryCookieProps,
  isObject,
  shallowEqual,
} from '@/helpers/common';
import { useCommonStore } from '@/store';

export function useShallowDepsCompare(deps: DependencyList | undefined = []) {
  const depsRef = useRef(deps);

  if (depsRef.current === deps) return depsRef.current;

  function reset() {
    depsRef.current = deps;
  }

  if (
    depsRef &&
    depsRef.current &&
    deps &&
    depsRef.current.length === deps.length
  ) {
    const differ = deps.some((newValue, index) => {
      if (depsRef.current == null) return true;

      const oldValue = depsRef.current[index];

      if (typeof oldValue !== typeof newValue) {
        return true;
      } else if (Array.isArray(newValue) || isObject(newValue)) {
        if (!shallowEqual(oldValue, newValue)) {
          return true;
        }
      } else if (oldValue !== newValue) {
        return true;
      }

      return false;
    });

    if (differ) reset();
  } else {
    reset();
  }

  return depsRef.current;
}

export function useShallowMemo<T>(
  factory: () => T,
  deps: DependencyList | undefined
): T {
  // eslint-disable-next-line
  return useMemo(factory, useShallowDepsCompare(deps));
}

export function useShallowCallback<T extends (...args: any[]) => any>(
  callback: T,
  deps: DependencyList
): T {
  return useShallowMemo(() => callback, deps);
}

export function useShallowCompareEffect(
  effect: EffectCallback,
  deps?: DependencyList
) {
  // eslint-disable-next-line
  useEffect(effect, useShallowDepsCompare(deps));
}

export function useMount() {
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
  }, []);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    []
  );

  return isMounted;
}

export function useDeepCompareMemo<T>(value: T) {
  const ref = useRef<T>(value);

  if (!isEqual(value, ref.current)) {
    ref.current = value;
  }

  return ref.current;
}

function isEqual<T>(a: T, b: T) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function useMutable<T>(value: T) {
  const ref = useRef(value);

  useEffect(() => {
    ref.current = value;
  });

  return ref;
}

export function useCreateScript(
  url: string,
  condition: boolean,
  isDefer?: boolean
) {
  useEffect(() => {
    if (condition == null || !condition) return;

    createScript(url, isDefer);

    return () => {
      deleteScript(url);
    };
  }, [url, condition, isDefer]);
}

export function useCreateNoscript(nosript: string, condition: boolean) {
  useEffect(() => {
    if (condition == null || !condition) return;

    const noscriptBlock = document.getElementById('noscript');

    if (noscriptBlock == null) return;

    noscriptBlock.innerHTML = nosript.trim();
  }, [nosript, condition]);
}

export function useToggle(
  initialValue = false
): [boolean, () => void, (value: boolean) => void] {
  const [value, setValue] = useState(initialValue);

  const toggle = useCallback(() => {
    setValue((v) => !v);
  }, []);

  const setState = useCallback((value: boolean) => {
    setValue(value);
  }, []);

  return [value, toggle, setState];
}

export function useElementUpdate<T>() {
  const [state, setState] = useState<T>();

  const ref = useCallback((node: T) => {
    setState(node);
  }, []);

  return { ref, stateElement: state };
}

export enum DeviceMode {
  PORTRAIT,
  LANDSCAPE,
}

export function useDeviceMode() {
  const [mode, setMode] = useState<DeviceMode>(DeviceMode.LANDSCAPE);

  useEffect(() => {
    const checkMode = () => {
      const result =
        window.innerHeight > window.innerWidth
          ? DeviceMode.PORTRAIT
          : DeviceMode.LANDSCAPE;

      setMode(result);
    };

    checkMode();

    window.addEventListener('resize', checkMode);

    return () => {
      window.removeEventListener('resize', checkMode);
    };
  });

  return mode;
}

interface UsePushParametersInWindowParams<T> {
  windowNameProperty: string;
  object: T | undefined;
  objectProperties: (keyof T)[];
}

export const usePushParametersInWindow = <T>({
  windowNameProperty,
  object,
  objectProperties,
}: UsePushParametersInWindowParams<T>) => {
  useEffect(() => {
    if (object == null) return;

    const data = objectProperties.reduce((acc, property) => {
      if (object[property] != null) {
        acc = { ...acc, [property]: object[property] };
      }

      return acc;
    }, {} as Partial<T>);

    const json = JSON.stringify(data);

    // @ts-ignore
    window[windowNameProperty] = json;
  }, [object, windowNameProperty, objectProperties]);
};

interface UseWindowClickProps {
  required?: number;
}

export function useWindowClick({
  required,
}: UseWindowClickProps): [number, boolean] {
  const [numberOfClicks, setNumberOfClicks] = useState(0);

  useEffect(() => {
    const handleCLick = () => {
      setNumberOfClicks((s) => s + 1);
    };

    document.addEventListener('click', handleCLick);

    return () => {
      document.addEventListener('click', handleCLick);
    };
  }, []);

  return [numberOfClicks, required ? numberOfClicks >= required : false];
}

export interface UseCookieProps extends FactoryCookieProps {
  active?: boolean | undefined;
}

export function useCookie(props: UseCookieProps) {
  useEffect(() => {
    if (props.active === false) return;

    factoryCookie(props);
  }, [props]);
}

export function useElementHeightOnResize() {
  const ref = useRef<any>(null);
  const [elementHeight, setElementHeight] = useState(0);

  useEffect(() => {
    const handleResize = () => {
      const el = ref.current;
      if (el) setElementHeight(el.offsetHeight);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  });

  return {
    ref,
    elementHeight,
  };
}

export const pickProperties = <T>(
  object: T | undefined,
  objectProperties: (keyof T)[]
) => {
  if (object == null) return;

  return objectProperties.reduce((acc, property) => {
    if (object[property] != null) {
      acc = { ...acc, [property]: object[property] };
    }
    return acc;
  }, {} as Partial<T>);
};

export function useHorizontalScroll() {
  const ref = useRef<HTMLElement | null>(null);
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  useEffect(() => {
    if (!isMobileDevice) {
      const el = ref.current;

      if (el) {
        const onWheel = (e: WheelEvent) => {
          if (+e.deltaY === 0) return;

          e.preventDefault();
          el.scrollTo({
            left: el.scrollLeft + e.deltaY,
          });
        };
        el.addEventListener('wheel', onWheel, false);
        return () => el.removeEventListener('wheel', onWheel, false);
      }
    }
  }, [isMobileDevice]);

  return ref;
}
